import React, { useState, useEffect } from 'react';
import packageJson from '../../package.json';
const APP_VERSION = process.env.REACT_APP_VERSION || packageJson.version;
import { useAuth } from '../context/AuthContext';
import { checkHealth } from '../api/client';

const inp = (focused, id) => ({
  width: '100%', padding: '11px 14px', borderRadius: 10,
  border: `1.5px solid ${focused === id ? 'var(--accent)' : 'var(--brd)'}`,
  background: 'var(--surf-alt)', color: 'var(--txt)',
  fontSize: 14, fontFamily: 'Heebo, sans-serif',
  outline: 'none', transition: 'border-color .18s',
  direction: 'ltr', textAlign: 'left',
});

const label = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: 'var(--muted)', marginBottom: 7, letterSpacing: .2,
};

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPw, setShowPw]     = useState(false);
  const [focused, setFocused]   = useState('');
  const [serverOk, setServerOk] = useState(null); // null = checking

  useEffect(() => {
    let active = true;
    const run = () => checkHealth()
      .then(({ ok }) => { if (active) setServerOk(!!ok); })
      .catch(() => { if (active) setServerOk(false); });
    run();
    const id = setInterval(run, 10000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('נא למלא שם משתמש וסיסמה');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(msg || 'שגיאה בהתחברות');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', padding: 20, fontFamily: 'Heebo, sans-serif', direction: 'rtl',
    }}>
      {/* Dot-grid background */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(circle at 1px 1px, var(--brd) 1px, transparent 0)',
        backgroundSize: '28px 28px', opacity: .6,
      }} />
      {/* Glow blob */}
      <div style={{
        position: 'fixed', top: '15%', left: '50%', transform: 'translateX(-50%)',
        width: 520, height: 320, borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(ellipse, rgba(37,99,235,0.09) 0%, transparent 70%)',
        filter: 'blur(40px)',
      }} />

      {/* Card */}
      <div style={{
        background: 'var(--surf)', borderRadius: 20,
        boxShadow: '0 2px 4px rgba(0,0,0,0.04), 0 20px 60px rgba(0,0,0,0.10)',
        border: '1px solid var(--brd)', padding: '44px 40px',
        width: '100%', maxWidth: 420, position: 'relative', zIndex: 1,
        animation: 'fadeUp .35s ease',
      }}>

        {/* Logo block */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 36 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, marginBottom: 14,
            boxShadow: '0 4px 16px rgba(37,99,235,0.25)',
          }}>🎬</div>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: -.5 }}>
            <span style={{ color: 'var(--accent)' }}>Zivlin</span>
            <span style={{ color: 'var(--txt)' }}>BoxOffice</span>
          </div>
        </div>

        {/* Heading */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--txt)', marginBottom: 4 }}>התחברות</h1>
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>הכנס את הפרטים שקיבלת בשביל להיכנס ל-ZivlinBoxOffice</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>

          {/* Username */}
          <div style={{ marginBottom: 16 }}>
            <label style={label}>שם משתמש</label>
            <input
              type="text"
              value={username}
              placeholder="username"
              onChange={e => setUsername(e.target.value)}
              onFocus={() => setFocused('username')}
              onBlur={() => setFocused('')}
              autoFocus
              autoComplete="username"
              style={inp(focused, 'username')}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 10 }}>
            <label style={label}>סיסמה</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                placeholder="••••••••"
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setFocused('password')}
                onBlur={() => setFocused('')}
                autoComplete="current-password"
                style={{ ...inp(focused, 'password'), paddingLeft: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                title={showPw ? 'הסתר סיסמה' : 'הצג סיסמה'}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--muted)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--faint)'}
                style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)',
                  lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center',
                  transition: 'color .15s',
                }}
              >
                {showPw ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 9, padding: '10px 14px', marginBottom: 18,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 14 }}>⚠</span>
              <span style={{ fontSize: 13, color: 'var(--danger)', fontWeight: 500 }}>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--accent-hov)'; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = 'var(--accent)'; }}
            onMouseDown={e => { if (!loading) e.currentTarget.style.transform = 'scale(.98)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'none'; }}
            style={{
              width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
              background: loading ? 'var(--faint)' : 'var(--accent)',
              color: '#ffffff', fontSize: 15, fontWeight: 700,
              fontFamily: 'Heebo, sans-serif',
              cursor: loading ? 'default' : 'pointer',
              transition: 'background .18s, transform .1s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: loading ? 'none' : '0 4px 14px rgba(37,99,235,0.25)',
            }}
          >
            {loading && (
              <span style={{
                width: 16, height: 16, borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                display: 'inline-block', animation: 'spin .7s linear infinite',
              }} />
            )}
            {loading ? 'מתחבר...' : 'כניסה'}
          </button>
        </form>

        {/* Footer */}
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--brd)', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, color: 'var(--faint)' }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
              background: serverOk == null ? '#f59e0b' : serverOk ? '#22c55e' : '#ef4444',
              animation: serverOk === false ? 'pulse 1.5s infinite' : 'none',
            }} />
            {serverOk == null ? 'בודק שרת...' : serverOk ? 'שרת מחובר' : 'שרת מנותק'} · v{APP_VERSION} · ZivlinBoxOffice
          </div>
        </div>

      </div>
    </div>
  );
}
